import { useCallback, useContext, useEffect, useState } from "react";

import { AdminRestriction } from "@safetyculture/s12-apis-connect-web/s12/restrictions/admin/v1/restrictions_pb";
import {
  EntitlementBucket,
  QuotaRestrictionConfiguration,
  Restriction,
  RestrictionName,
  RestrictionQuota,
} from "@safetyculture/s12-apis-connect-web/s12/restrictions/v1/common_pb";

import {
  getOrgRestrictionConfiguration,
  getOrgRestrictions,
} from "api/endpoints/restrictions";
import { callOptionsHeaders } from "api/helpers";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";

export const AI_CREDIT_RESTRICTION_NAME = `RESTRICTION_NAME_${
  RestrictionName[RestrictionName.AI_CREDIT_USAGE]
}`;
export interface UsageWindow {
  from: Date;
  to: Date;
}

export interface OrgQuotaBucket {
  key: string;
  label: string;
  quota: RestrictionQuota;
}

export const monthToDateWindow = (): UsageWindow => {
  const to = new Date();
  const from = new Date(to.getFullYear(), to.getMonth(), 1, 0, 0, 0, 0);
  return { from, to };
};

export const friendlyBucketLabel = (type: EntitlementBucket): string => {
  // EntitlementBucket[type] is undefined for any value not in the generated enum
  // (e.g. a new server-side bucket the client hasn't been regenerated for).
  // Map those to "Unknown" rather than mislabelling them as "Unspecified".
  const name = EntitlementBucket[type];
  if (name === undefined) return "Unknown";
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

// Finds a quota by its entitlement bucket type. The server keys the quota map by
// ULID and carries the logical bucket in `quotaType`, so identification is by type.
export const quotaByBucketType = (
  quotas: Record<string, RestrictionQuota>,
  type: EntitlementBucket,
): RestrictionQuota | undefined =>
  Object.values(quotas).find((q) => q.quotaType === type);

export const orgQuotaBuckets = (
  restriction?: AdminRestriction,
): OrgQuotaBucket[] => {
  if (restriction?.restriction.case !== "quotaMap") return [];

  return (
    Object.entries(restriction.restriction.value.quotas)
      .filter(([, q]) => !!q.userRestriction)
      // Some buckets arrive with quotaType left UNSPECIFIED (e.g. a default
      // ENTITLEMENT_BUCKET_OVERAGE the API leaks when no real overage exists).
      // Filter on quotaType rather than key shape: real buckets are correctly
      // typed regardless of whether they're ULID-keyed or legacy-string-keyed.
      .filter(
        ([, q]) =>
          q.userRestriction?.quotaType !== EntitlementBucket.UNSPECIFIED,
      )
      .map(([key, q]) => {
        const quota = q.userRestriction as RestrictionQuota;
        return { key, label: friendlyBucketLabel(quota.quotaType), quota };
      })
      .sort((a, b) => {
        if (a.quota.rank !== b.quota.rank) return a.quota.rank - b.quota.rank;
        const aTime = Number(a.quota.resetTime?.seconds ?? BigInt(0));
        const bTime = Number(b.quota.resetTime?.seconds ?? BigInt(0));
        return aTime - bTime;
      })
  );
};

// Per-user restriction: quotaMap of RestrictionQuota keyed by entitlement bucket.
export const userQuotaByBucket = (
  restriction?: Restriction,
): Record<string, RestrictionQuota> => {
  if (restriction?.restriction.case !== "quotaMap") return {};
  return restriction.restriction.value.quotas;
};

export const quotaUsage = (quota?: RestrictionQuota): number => {
  if (!quota) return 0;
  // Prefer the explicit usage field; fall back to limit - remaining.
  return quota.usage || Math.max(quota.limit - quota.remaining, 0);
};

// The per-user list RPC requires an explicit window and does not derive one.
// The org quota leaf carries the real billing period (startTime/resetTime) so
// the user list reconciles with the org's billing cycle; fall back to
// month-to-date only when the org quota carries no period.
//
// Caveat: this returns the FIRST bucket (sorted by rank, then resetTime), which
// may be a one-off credit pack whose expiry differs from the recurring plan
// period. For the recurring period prefer usageWindowFromConfig below.
export const usageWindowFromOrgRestriction = (
  restriction?: AdminRestriction,
): UsageWindow => {
  for (const { quota } of orgQuotaBuckets(restriction)) {
    const from = quota.startTime?.toDate();
    const to = quota.resetTime?.toDate();
    if (from && to) return { from, to };
  }
  return monthToDateWindow();
};

// The Plan configuration carries the authoritative recurring credit period
// (currentPeriodStart/currentPeriodEnd). Prefer it over the bucket-derived
// window: one-off credit packs (e.g. a Welcome grant with a 3-month expiry)
// have their own arbitrary window and must not define the reset cadence.
// Returns undefined when the config is absent — e.g. the caller lacks
// GET_ORG_RESTRICTION_CONFIGURATION or it is still loading — so callers can
// fall back to the bucket-derived window.
export const usageWindowFromConfig = (
  config?: QuotaRestrictionConfiguration,
): UsageWindow | undefined => {
  const from = config?.currentPeriodStart?.toDate();
  const to = config?.currentPeriodEnd?.toDate();
  if (from && to) return { from, to };
  return undefined;
};

export interface OrgAICredit {
  isLoading: boolean;
  isReady: boolean;
  orgRestriction?: AdminRestriction;
  usageWindow: UsageWindow;
  refresh: () => void;
}

// Fetches the org-level AI credit quota once and derives the usage window from
// its billing period. Shared by the AI Settings overage rows and the per-user
// Usage list so both read from a single fetch.
export const useOrgAICredit = (
  roleId: string,
  regionCode: string,
  enabled: boolean,
): OrgAICredit => {
  const { showError } = useContext(SnackbarContext);

  const [isLoading, setIsLoading] = useState(enabled);
  const [isReady, setIsReady] = useState(false);
  const [orgRestriction, setOrgRestriction] = useState<AdminRestriction>();
  const [usageWindow, setUsageWindow] =
    useState<UsageWindow>(monthToDateWindow());

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getOrgRestrictions(
        {
          organizationId: toUUID(roleId),
          restrictionNames: [AI_CREDIT_RESTRICTION_NAME],
        },
        callOptionsHeaders(regionCode, roleId),
      );
      const restriction = response.restrictions[AI_CREDIT_RESTRICTION_NAME];
      setOrgRestriction(restriction);
      setUsageWindow(usageWindowFromOrgRestriction(restriction));
    } catch (err) {
      showError(err as GrpcError);
    } finally {
      setIsLoading(false);
      setIsReady(true);
    }
  }, [roleId, regionCode, showError]);

  useEffect(() => {
    if (!enabled) return;
    load();
  }, [enabled, load]);

  return { isLoading, isReady, orgRestriction, usageWindow, refresh: load };
};

export interface OrgAICreditConfig {
  isLoading: boolean;
  config?: QuotaRestrictionConfiguration;
}

// Fetches the org-level Plan configuration (billing period and the
// configured per-bucket allowances). Gated separately from usage since it is a
// distinct permission.
export const useOrgAICreditConfig = (
  roleId: string,
  regionCode: string,
  enabled: boolean,
): OrgAICreditConfig => {
  const { showError } = useContext(SnackbarContext);

  const [isLoading, setIsLoading] = useState(enabled);
  const [config, setConfig] = useState<QuotaRestrictionConfiguration>();

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getOrgRestrictionConfiguration(
        {
          organizationId: toUUID(roleId),
          restrictionName: RestrictionName.AI_CREDIT_USAGE,
        },
        callOptionsHeaders(regionCode, roleId),
      );
      const configuration = response.configuration?.configuration;
      setConfig(
        configuration?.case === "quota" ? configuration.value : undefined,
      );
    } catch (err) {
      showError(err as GrpcError);
    } finally {
      setIsLoading(false);
    }
  }, [roleId, regionCode, showError]);

  useEffect(() => {
    if (!enabled) return;
    load();
  }, [enabled, load]);

  return { isLoading, config };
};
